import React, { useState } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Stepper, Step, StepLabel,
  InputAdornment, IconButton, CircularProgress, Alert, Avatar, MenuItem, Grid, Divider,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Church, CloudUpload, ArrowForward, ArrowBack,
  CheckCircle, AccountBalance, VerifiedUser, Insights,
} from '@mui/icons-material';
import axios from 'axios';
import { setupInitURL } from '../BaseURL';
import { useNotify } from '../notify';

const STEPS = [
  { label: 'Organization', description: 'Basic details about your church' },
  { label: 'Administrator', description: 'Your first user account' },
  { label: 'Confirm',       description: 'Review and finish' },
];
const CURRENCIES = ['GHS', 'USD', 'EUR', 'GBP', 'NGN', 'KES', 'ZAR'];

const FEATURES = [
  { icon: <AccountBalance sx={{ fontSize: 20 }} />, title: 'Cash Book & Ledger', desc: 'Track every income and payment.' },
  { icon: <VerifiedUser  sx={{ fontSize: 20 }} />, title: 'Role-based Access',   desc: 'Admins, accountants and viewers.' },
  { icon: <Insights      sx={{ fontSize: 20 }} />, title: 'Real-time Insights',  desc: 'Monthly balances at a glance.' },
];

export default function Setup({ onDone }) {
  const notify                  = useNotify();
  const [step, setStep]         = useState(0);
  const [loading, setLoading]   = useState(false);
  const [showPwd, setShowPwd]   = useState(false);

  const [church, setChurch] = useState({
    name: '', address: '', phone: '', email: '', currency: 'GHS', logo: '',
  });
  const [admin, setAdmin] = useState({
    full_name: '', username: '', email: '', password: '', confirm: '',
  });

  const changeChurch = (e) => setChurch({ ...church, [e.target.name]: e.target.value });
  const changeAdmin  = (e) => setAdmin({ ...admin, [e.target.name]: e.target.value });

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      notify.warning('Logo must be under 500 KB.', { title: 'File too large' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setChurch(c => ({ ...c, logo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const validateStep = () => {
    const fail = (msg) => { notify.warning(msg, { title: 'Missing details' }); return false; };
    if (step === 0) {
      if (!church.name.trim()) return fail('Church name is required.');
    }
    if (step === 1) {
      if (!admin.full_name.trim() || !admin.username.trim() || !admin.password) {
        return fail('Full name, username and password are required.');
      }
      if (admin.password.length < 6) return fail('Password must be at least 6 characters.');
      if (admin.password !== admin.confirm) return fail('Passwords do not match.');
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(setupInitURL, {
        church: {
          name: church.name.trim(),
          address: church.address.trim(),
          phone: church.phone.trim(),
          email: church.email.trim(),
          currency: church.currency,
          logo: church.logo || null,
        },
        admin: {
          full_name: admin.full_name.trim(),
          username: admin.username.trim(),
          email: admin.email.trim(),
          password: admin.password,
        },
      });
      if (res.data.success) {
        notify.success('Your workspace is ready. You can sign in now.', { title: 'Setup complete' });
        onDone();
      }
    } catch (err) {
      notify.error(err.response?.data?.message || 'Setup failed.', { title: 'Setup error' });
    } finally {
      setLoading(false);
    }
  };

  const stepContent = (
    <Box>
      {step === 0 && (
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Tell us about your church</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This information appears on the login page and in your printed reports.
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Church Name" name="name"
                placeholder="e.g. Grace Chapel International"
                value={church.name} onChange={changeChurch} size="medium"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Address" name="address"
                placeholder="Street, City, Country"
                value={church.address} onChange={changeChurch} size="medium"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Phone" name="phone"
                value={church.phone} onChange={changeChurch} size="medium"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Email" name="email" type="email"
                value={church.email} onChange={changeChurch} size="medium"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Base Currency" name="currency"
                value={church.currency} onChange={changeChurch} size="medium"
              >
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth component="label" variant="outlined"
                startIcon={<CloudUpload />} sx={{ py: 1.65, height: '100%' }}
              >
                {church.logo ? 'Change Logo' : 'Upload Logo'}
                <input type="file" hidden accept="image/*" onChange={handleLogo} />
              </Button>
            </Grid>
            {church.logo && (
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <Avatar src={church.logo} sx={{ width: 80, height: 80, mx: 'auto', border: '3px solid #E2E8F0' }} />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {step === 1 && (
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Create your administrator</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This account has full access. You can add accountants and viewers later.
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Full Name" name="full_name"
                value={admin.full_name} onChange={changeAdmin} size="medium"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Username" name="username"
                value={admin.username} onChange={changeAdmin} size="medium"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Email" name="email" type="email"
                value={admin.email} onChange={changeAdmin} size="medium"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Password" name="password"
                type={showPwd ? 'text' : 'password'}
                value={admin.password} onChange={changeAdmin} size="medium"
                helperText="At least 6 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPwd(!showPwd)} edge="end" size="small">
                        {showPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Confirm Password" name="confirm"
                type={showPwd ? 'text' : 'password'}
                value={admin.confirm} onChange={changeAdmin} size="medium"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {step === 2 && (
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Review your setup</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Double-check everything before we create your workspace.
          </Typography>

          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderColor: '#E2E8F0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {church.logo ? (
                <Avatar src={church.logo} sx={{ width: 56, height: 56 }} />
              ) : (
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}><Church /></Avatar>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>Organization</Typography>
                <Typography variant="h6" fontWeight={700}>{church.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {[church.address, church.phone, church.email].filter(Boolean).join(' · ') || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">Currency: {church.currency}</Typography>
              </Box>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderColor: '#E2E8F0' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>Administrator</Typography>
            <Typography variant="h6" fontWeight={700}>{admin.full_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              @{admin.username}{admin.email && ` · ${admin.email}`}
            </Typography>
          </Paper>

          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
            You can edit church details, add users, and configure accounts anytime from <b>Settings</b>.
          </Alert>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', background: '#F5F7FA' }}>
      {/* Left branding panel (desktop) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: 420,
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 5,
          background: 'linear-gradient(160deg, #0B2545 0%, #13315C 60%, #061529 100%)',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', top: -80, right: -80,
          width: 260, height: 260, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(200,146,17,0.25) 0%, transparent 70%)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -100, left: -100,
          width: 300, height: 300, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
        }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 6 }}>
            <Box sx={{
              width: 42, height: 42, borderRadius: 2,
              bgcolor: '#C89211',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(200,146,17,0.35)',
            }}>
              <Church sx={{ fontSize: 22, color: '#fff' }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '0.02em' }}>
              The Church
            </Typography>
          </Box>

          <Typography variant="h4" fontWeight={800} sx={{ mb: 1.5, letterSpacing: '-0.02em' }}>
            Set up your accounting workspace
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, mb: 5 }}>
            A clean, purpose-built ledger for church finances — income, payments and monthly balances in one place.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {FEATURES.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{
                  width: 34, height: 34, borderRadius: 1.5,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#E0A82E', flexShrink: 0,
                }}>
                  {f.icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{f.title}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{f.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', position: 'relative', zIndex: 1 }}>
          © {new Date().getFullYear()} — Church Accounting System
        </Typography>
      </Box>

      {/* Right form panel */}
      <Box sx={{
        flex: 1, display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'center',
        p: { xs: 2, sm: 3, md: 6 },
        overflowY: 'auto',
      }}>
        <Box sx={{ width: '100%', maxWidth: 560 }}>
          {/* Mobile branding */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 1.5,
              bgcolor: 'primary.main',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Church sx={{ fontSize: 22, color: '#fff' }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700}>The Church</Typography>
          </Box>

          {/* Step meta */}
          <Typography variant="caption" color="secondary.main" fontWeight={700} sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Step {step + 1} of {STEPS.length}
          </Typography>
          <Box sx={{ mt: 0.5, mb: { xs: 2, sm: 3 } }}>
            <Stepper activeStep={step} alternativeLabel sx={{
              '& .MuiStepLabel-label': {
                fontSize: { xs: 10.5, sm: 12 }, fontWeight: 600, mt: 0.5,
              },
              '& .MuiStepIcon-root': { fontSize: { xs: 22, sm: 26 } },
              '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
              '& .MuiStepIcon-root.Mui-completed': { color: 'secondary.main' },
              px: { xs: 0, sm: 1 },
            }}>
              {STEPS.map(s => (
                <Step key={s.label}><StepLabel>{s.label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>

          <Divider sx={{ mb: 3 }} />


          {stepContent}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid #E2E8F0' }}>
            <Button
              onClick={back} disabled={step === 0 || loading}
              startIcon={<ArrowBack />} size="large" color="inherit"
            >
              Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                variant="contained" onClick={next} endIcon={<ArrowForward />}
                size="large" sx={{ px: 3 }}
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="contained" onClick={submit} disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                size="large" sx={{ px: 3 }}
              >
                {loading ? 'Setting up…' : 'Complete Setup'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
